import { useState } from 'react';
import { exportPackages, exportFormats, postPreviewItems } from '@/mocks/contentEngineImplementation';

export default function ExportDownloadCenter() {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState('single-post');
  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => setExporting(false), 1500);
  };

  const pkg = exportPackages.find((p) => p.id === selectedPackage);

  return (
    <div className="space-y-6">
      {/* Export Format Selector */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Export & Download Center</h2>
            <p className="text-sm text-gray-500 mt-1">Package and export content in your preferred format</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {exportFormats.map((fmt) => (
            <button
              key={fmt.id}
              onClick={() => setSelectedFormat(fmt.id)}
              className={`p-4 rounded-xl border text-left transition-all ${
                selectedFormat === fmt.id
                  ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                <i className={`${fmt.icon} text-xl text-gray-600`}></i>
              </div>
              <h4 className="text-sm font-semibold text-gray-900">{fmt.name}</h4>
              <p className="text-xs text-gray-500 mt-1">{fmt.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Packages List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Available Packages</h3>
            <span className="text-sm text-gray-500">{exportPackages.length} packages ready</span>
          </div>

          <div className="space-y-3">
            {exportPackages.map((packageItem) => (
              <div
                key={packageItem.id}
                onClick={() => setSelectedPackage(packageItem.id)}
                className={`p-5 rounded-xl border cursor-pointer transition-all ${
                  selectedPackage === packageItem.id
                    ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${
                        packageItem.type === 'Monthly Calendar' ? 'ri-calendar-2-line' :
                        packageItem.type === 'Campaign Bundle' ? 'ri-folder-zip-line' :
                        'ri-file-list-3-line'
                      } text-2xl text-gray-600`}></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">{packageItem.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{packageItem.type} | {packageItem.posts} posts | {packageItem.size}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {packageItem.platforms.map((platform) => (
                          <span key={platform} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            {platform}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        {packageItem.formats.map((format) => (
                          <span key={format} className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 text-gray-500">
                            {format}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs text-gray-400">Updated {packageItem.lastUpdated}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport();
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      {exporting && selectedPackage === packageItem.id ? (
                        <span className="flex items-center gap-1.5">
                          <i className="ri-loader-4-line animate-spin"></i> Exporting...
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <i className="ri-download-line"></i> Download
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Package Preview</h3>

          {pkg ? (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <i className="ri-file-text-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{pkg.name}</h4>
                  <p className="text-xs text-gray-500">{pkg.posts} posts | {pkg.size}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Contents</p>
                <div className="space-y-2">
                  {postPreviewItems.map((item) => (
                    <div key={item.field} className="flex items-start gap-2">
                      <span className="text-xs font-medium text-gray-500 w-24 flex-shrink-0">{item.field}</span>
                      <span className="text-xs text-gray-700 line-clamp-2">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Platforms</p>
                <div className="flex flex-wrap gap-1.5">
                  {pkg.platforms.map((p) => (
                    <span key={p} className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-700 font-medium">
                      {p}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Formats</p>
                <div className="flex flex-wrap gap-1.5">
                  {pkg.formats.map((f) => (
                    <span key={f} className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600">
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-2">
                <button
                  onClick={handleExport}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  {exporting ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin"></i> Generating...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-download-line"></i> Download Package
                    </span>
                  )}
                </button>
                <button className="w-full py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap">
                  <i className="ri-share-line mr-1.5"></i>Share with Team
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-list-3-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm font-medium text-gray-600">Select a package to preview</p>
              <p className="text-xs text-gray-400 mt-1">Click any package from the list to see contents</p>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Export Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-file-text-line text-xl text-gray-600"></i>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">Download Single Post</h4>
            <p className="text-xs text-gray-500 mt-1">Caption + media + metadata</p>
          </button>
          <button className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-folder-zip-line text-xl text-gray-600"></i>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">Download Campaign</h4>
            <p className="text-xs text-gray-500 mt-1">All posts from a campaign</p>
          </button>
          <button className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-calendar-2-line text-xl text-gray-600"></i>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">Download Full Month</h4>
            <p className="text-xs text-gray-500 mt-1">Complete calendar + assets</p>
          </button>
          <button className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-file-list-3-line text-xl text-gray-600"></i>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">CSV Export</h4>
            <p className="text-xs text-gray-500 mt-1">Structured data for tracking</p>
          </button>
        </div>
      </div>
    </div>
  );
}